
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { Box } from "@mui/material";
import { ContentView } from "../../components/content-view/ContentView";
import { ContentContainer } from "../../components/content-container/ContentContainer";
import { ContentSection } from "../../components/content-section/ContentSection";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import useAlarmSetpointReportState from "./AlarmSetpointReportState";
import { useEffect } from "react";
import AlarmSetpointReportUtils from "./AlarmSetpointReportUtils";
import { t } from "i18next";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import NoData from "../../components/no-data/NoData";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import _ from "lodash";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import UUID from "../../common/utilities/Uuid";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import AdminReportDataService from "../../services/admin-report-data-service/AdminReportDataService";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { RestError } from "../../services/rest-service/RestService";
import { useOrganizations } from "../../queries/organizations/UseOrganizations";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import { IOrganization } from "../../services/organization-data-service/Models";
import { AgGridEvent } from "ag-grid-community";
import { useAlarmSetpointReport } from "../../queries/admin-reports/UseAlarmSetpointReport";
import { ISortParameters } from "../../common/models/ISortParameters";
import SideSheetRadioGroup from "../../components/side-sheet/side-sheet-radio-group/SideSheetRadioGroup";
import DefaultGrid from "../../components/default-grid/DefaultGrid";
import { AutoSizeStrategyEnum } from "../../components/default-grid/InternalGrid";


export default function AlarmSetpointReportView() {

    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    const [, setAppNotificationState] = useAppNotificationContext();

    const [progressState, actionStarted] = useProgressContext();


    const {
        state,
        setOrganization,
        setData,
        setActions,
        setDownloadUid,
        setGridApi,
        setIncludeInActive,
        //resetState
    } = useAlarmSetpointReportState();


    const { data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const { data: organizations } = useQueryNotifications(useOrganizations)({
        includeAllOption: true
    });

    const organization = organizations?.find(o => o.id === (state.organizationID ?? group?.organizationID));

    const { data, status, refetch } = useQueryNotifications(useAlarmSetpointReport)({
        organizationID: state.organizationID ?? -1,
        contextGroupID: organization?.peerGroupID ?? routeContext.groupID as number,
        includeInActiveAndInvisibleDevices: state.includeInActiveAndInvisibleDevices,
        enabled: state.organizationID !== undefined
    });

    const onExport = (mimeType: ApplicationMimeTypesEnum) => {

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: t('views.setpointReport'),
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            const columnState = state.gridApi?.getColumnState();

            const sortItem = columnState?.find(o => !!o.sort);


            const sortParams: ISortParameters = {
                propertyName: sortItem?.colId ?? '',
                sortAscending: (sortItem?.sort ?? 'asc') === 'asc'
            };


            AdminReportDataService.exportAlarmSetpointReport(uid,
                state.organizationID as number,
                routeContext.groupID as number,
                state.includeInActiveAndInvisibleDevices,
                mimeType,
                sortParams
            );

            const items = [...state.actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export);
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    };


    function onExecuteAction(menuItem: ActionMenuItem) {

        const item = menuItem.action;

        switch (item) {
            case BaseActionEnum.Refresh:
                refetch();
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel);
                break;
        }
    }

    function onResetOptions() {
        //resetState();
    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    function onGridReady(event: AgGridEvent) {
        setGridApi(event.api);
    }

    useEffect(() => {

        if (data) {

            const cols = AlarmSetpointReportUtils.createGridColumns();
            const rows = AlarmSetpointReportUtils.createGridRows(data);

            setData({
                cols,
                rows
            });
        }
    }, [
        data,
        setData,
    ]);


    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (progress?.uid === state.downloadUid &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
                progress?.notificationMsg.status === StatusEnum.Failed)) {
            setDownloadUid(undefined);
        }
    }, [
        progress,
        state.downloadUid,
        setDownloadUid
    ]);


    useEffect(() => {
        if (organization?.id && organization?.id !== state.organizationID) {
            setOrganization(organization.id);
        }
    }, [
        organization?.id,
        organization?.peerGroupID,
        state.organizationID,
        setOrganization
    ]);

    useEffect(() => {
        const actions = _.cloneDeep(state.actions);

        const exportAction = actions.find(o => o.action === BaseActionEnum.Export);

        if (exportAction) {
            exportAction.disabled = progress && progress.uid === state.downloadUid;
        }

        // if (!_.isEqual(state.actions, actions)) {
            setActions(actions);
        // }
    }, [
        progress,
        state.actions,
        state.downloadUid,
        setActions
    ]);

    return (
        <ContentView>
            <ContextualAppBar
                hideBreadcrumb={true}
                actions={state.actions}
                viewOptionState={state.viewOptionState}
                onExecuteAction={onExecuteAction}
                showOrganizationPicker={true}
                onOrganizationSelected={onOrganizationChanged}
                organizationID={state.organizationID}
            />

            <LoadingZone isLoading={status === 'loading'}>
                <ContentContainer>
                    <ContentSection>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                margin: '1rem',
                                overflow: "hidden"
                            }}>
                            {status === 'success' && (state.data?.rows.length ?? 0) === 0 &&
                                <NoData
                                    title={t('alarmSetpointReport.nodata.title')}
                                    text={t('alarmSetpointReport.nodata.text')}
                                />
                            }

                            {state.data && state.data.rows.length > 0 &&

                                <DefaultGrid
                                    onGridReady={onGridReady}
                                    suppressColumnVirtualisation={true}
                                    suppressMultiSort={true}
                                    rowData={state.data.rows}
                                    defaultColDef={{
                                        autoHeaderHeight: true,
                                        sortable: true,
                                        filter: "agMultiColumnFilter",
                                        resizable: true,
                                        // SPR 04-08-2025 - Removed autoHeight and wrapText from defaultColDef
                                        // Use multiline column type in column definitions, instead
                                        cellStyle: {
                                            wordBreak: 'normal',
                                        }
                                    }}
                                    columnDefs={state.data.cols}
                                    autoSizeStrategy={AutoSizeStrategyEnum.None}
                                />
                            }
                        </Box>
                    </ContentSection>

                    <OptionsSideSheet onReset={onResetOptions}>
                        <SideSheetRadioGroup
                            //label={t('setpointAdmin.notificationType')}
                            value={state.includeInActiveAndInvisibleDevices ? 1 : 0}
                            radioButtons={[{
                                label: t('alarmSetpointReport.excludeinactiveorhiddenDevice'),
                                ariaLabel: t('alarmSetpointReport.excludeinactiveorhiddenDevice'),
                                value: 0
                            }, {
                                label: t('alarmSetpointReport.includeinactiveorhiddenDevice'),
                                ariaLabel: t('alarmSetpointReport.includeinactiveorhiddenDevice'),
                                value: 1
                            }]}
                            onChange={(event) => setIncludeInActive((+event.target.value) ? true : false)}
                        />
                    </OptionsSideSheet>
                </ContentContainer>
            </LoadingZone>
        </ContentView>
    );
}
